import { useLocation, matchPath } from "react-router-dom";
import _ from "lodash";
import { Features } from "@/features";
import i18n from "@/lib/i18n";

// Helper to join paths safely
const joinPaths = (path1: string, path2: string) => {
    const p1 = path1.endsWith('/') ? path1.slice(0, -1) : path1;
    const p2 = path2.startsWith('/') ? path2.slice(1) : path2;
    return `${p1}/${p2}`;
};

// Recursively flatten routes with their full absolute paths
const flattenRoutes = (routes: any[], parentPath: string = ''): any[] => {
    let flattened: any[] = [];
    
    routes.forEach(route => {
        let currentPath = parentPath;
        
        if (route.path) {
            // If path matches absolute (starts with /), reset parent (though strictly in v6 config nesting usually appends)
            // But here we assume appending behavior for standard nesting
             if (route.path.startsWith('/') && parentPath === '') {
                 currentPath = route.path;
             } else {
                 currentPath = joinPaths(parentPath, route.path);
             }
        }
        
        // Add current route to flattened list if it has a path or is index
        // We prioritise adding the child version if it exists
        flattened.push({ ...route, fullPath: currentPath });

        if (route.children && route.children.length > 0) {
            flattened = flattened.concat(flattenRoutes(route.children, currentPath));
        }
    });

    return flattened;
};

export const useRouteMetadata = () => {
    const location = useLocation();
    
    // Flatten all routes from all features
    const topLevelRoutes = _.flatMap(Features as any[], (feature) => feature.routes || []);
    const allRoutes = flattenRoutes(topLevelRoutes);
    
    // Find the most specific match (deepest matching route usually or just an exact match)
    // We prioritize an EXACT match of the pathname
    let matchedRoute = allRoutes.find((route: any) => 
        route.fullPath && matchPath({ path: route.fullPath, end: true }, location.pathname)
    );

    // If no exact match, fallback to finding a parent (less specific)
    if (!matchedRoute) {
        matchedRoute = allRoutes.find((route: any) => 
            route.fullPath && location.pathname.startsWith(route.fullPath) && route.fullPath !== '/'
        );
    }

    return {
        title: matchedRoute?.meta?.label ? i18n.t(matchedRoute.meta.label) : (matchedRoute?.title || "BLS"),
        description: matchedRoute?.meta?.description || "",
        icon: matchedRoute?.meta?.icon,
        showHeader: matchedRoute?.meta?.showHeader !== false, // Default to true if not specified
    };
};
